/**
 * Mock data service to provide fallback data when the API fails or returns empty data
 */

import { ApiLocation, SensorReadings, SensorData } from './api';
import { sites } from '../data/sites';

function toLocationId(siteId: string, fallbackIndex: number): number {
  const digitsOnly = siteId.replace(/\D/g, '');
  const parsed = Number(digitsOnly);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallbackIndex + 1;
}

/**
 * Get mock locations derived from static site data
 */
export function getMockLocations(): ApiLocation[] {
  return sites.map((site, index) => ({
    ID: toLocationId(site.id, index),
    gCoord: {
      x: site.coordinates[0],
      y: site.coordinates[1],
    },
    siteName: site.name,
    siteDescription: site.description,
  }));
}

/**
 * Generates fake timestamps starting from the current time and going back
 * @param count - Number of timestamps to generate
 * @param intervalMinutes - Minutes between each reading
 */
function generateTimestamps(count: number, intervalMinutes: number = 30): string[] {
  const now = new Date();
  const timestamps: string[] = [];
  
  for (let i = 0; i < count; i++) {
    const time = new Date(now.getTime() - i * intervalMinutes * 60 * 1000);
    timestamps.push(time.toISOString());
  }
  
  return timestamps;
}

/**
 * Generates mock sensor data
 * @param baseValue - Base value for the readings
 * @param variance - Maximum random variance from the base value
 * @param count - Number of readings to generate
 */
function generateMockReadings(
  sensorID: number,
  locationID: number,
  sensorName: string,
  baseValue: number,
  variance: number,
  count: number
): SensorData[] {
  const timestamps = generateTimestamps(count);
  
  return timestamps.map((timestamp, index) => {
    // Random value with some variance to simulate real data
    const randomVariance = (Math.random() * 2 - 1) * variance;
    // Add a slight trend over time
    const trendFactor = (index / count) * variance * 0.5;
    const value = baseValue + randomVariance + trendFactor;
    
    return {
      timestamp,
      value: Number(value.toFixed(2)),
      sensorID,
      locationID,
      sensorName,
      unit: ''
    };
  });
}

/**
 * Get mock sensor data for a location
 */
export function getMockSensorData(locationId: number): SensorReadings {
  // Base values and unit information for different sensor types
  const sensorConfigs = {
    TEMPERATURE: { baseValue: 24.5, variance: 1.5, unit: '°C' },
    SALINITY: { baseValue: 35, variance: 2, unit: 'PSU' },
    TURBIDITY: { baseValue: 2.8, variance: 0.5, unit: 'NTU' },
    pH: { baseValue: 8.2, variance: 0.3, unit: '' },
    'DISSOLVED OXYGEN': { baseValue: 6.8, variance: 0.5, unit: 'mg/L' }
  };
  
  const mockReadings: SensorReadings = {};
  
  // Generate mock data for each sensor type
  Object.entries(sensorConfigs).forEach(([sensorName, config], index) => {
    const readings = generateMockReadings(
      index + 1, // sensorID
      locationId,
      sensorName,
      config.baseValue,
      config.variance,
      12 // number of readings
    );
    
    const latestReading = readings[0];
    let latestValue = latestReading ? latestReading.value.toString() : 'N/A';
    
    // Add unit to latest value
    if (config.unit) {
      latestValue = `${latestValue}${config.unit}`;
    }
    
    mockReadings[sensorName] = {
      readings,
      latestValue,
      rawAverage: null,
      rawLatest: latestReading ? latestReading.value : null,
      unit: config.unit,
      latestValidTimestamp: latestReading ? latestReading.timestamp : null
    };
  });
  
  return mockReadings;
}

/**
 * Get mock sensor data or merge it with partial API data
 * @param locationId - The location ID
 * @param apiData - Partial API data to fill in missing values for
 */
export function getMockOrMergedData(locationId: number, apiData?: SensorReadings): SensorReadings {
  const mockData = getMockSensorData(locationId);
  
  // If no API data, just return mock data
  if (!apiData || Object.keys(apiData).length === 0) {
    console.info('Using mock data - no API data available');
    return mockData;
  }
  
  // Merge data - use API data where available, fill in with mock data where missing
  const mergedData: SensorReadings = {};
  
  // First add all API data
  Object.entries(apiData).forEach(([key, value]) => {
    mergedData[key] = value;
  });
  
  // Then fill in missing sensor types with mock data
  Object.entries(mockData).forEach(([key, value]) => {
    if (!mergedData[key]) {
      console.info(`Adding mock data for missing sensor: ${key}`);
      mergedData[key] = value;
    }
  });
  
  return mergedData;
} 